/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useMediaQuery, useTheme } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import { ICustomTheme } from 'interfaces';
import euroIcon from 'assets/icons/euro.svg';
import numbersIcon from 'assets/icons/nombres.svg';
import Value from 'components/ProductsDetailsList/Value';
import {
  StyledDataContainer,
  StyledDataItem,
  StyledDesktopView,
  StyledLeftContainer,
  StyledMobileDataContainer,
  StyledMobileDataItem,
  StyledMobilePictoContainer,
  StyledMobileSummaryCategory,
  StyledMobileView,
  StyledNumberOfResultsDeclared,
  StyledOverviewContainer,
  StyledPicto,
  StyledRightContainer,
  StyledSummaryCategory,
  StyledUnitToDisplay,
  StyledIcon,
} from './styles';

export const Overview = ({
  icon,
  title,
  statement,
  basicOverviewStatement,
  totalDeclarationQuantityPreview,
  totalDeclarationFinancialValuePreview,
  totalDeclarationRewardPreview,
  totalDeclarationResultPreview,
  displayFinancialValues,
  expandArrowIcon,
  isGoalMecanicByProduct,
  unitToDisplay,
}: {
  className?: string;
  icon: JSX.Element;
  title: string;
  statement?: JSX.Element | null;
  basicOverviewStatement?: JSX.Element | null;
  totalDeclarationQuantityPreview?: number;
  totalDeclarationFinancialValuePreview?: number;
  totalDeclarationRewardPreview?: number;
  totalDeclarationResultPreview?: number;
  displayFinancialValues?: boolean;
  expandArrowIcon?: JSX.Element;
  isGoalMecanicByProduct?: boolean;
  unitToDisplay?: string | null;
}): JSX.Element => {
  const isMobile = useMediaQuery((theme: ICustomTheme) =>
    theme.breakpoints.down('sm'),
  );
  const { t } = useTranslation();
  const theme: ICustomTheme = useTheme();

  return (
    <StyledOverviewContainer>
      {isMobile ? (
        <StyledMobileView>
          <StyledMobilePictoContainer>
            <StyledIcon>{icon}</StyledIcon>
            <StyledMobileSummaryCategory>{title}</StyledMobileSummaryCategory>
          </StyledMobilePictoContainer>
          {basicOverviewStatement && (
            <StyledNumberOfResultsDeclared>
              {basicOverviewStatement}
            </StyledNumberOfResultsDeclared>
          )}
          <StyledMobileDataContainer>
            {!!totalDeclarationQuantityPreview && (
              <StyledMobileDataItem>
                <Value
                  value={totalDeclarationQuantityPreview}
                  imgProps={{ src: numbersIcon, alt: 'quantity icon' }}
                  textProps={{
                    color: 'secondary',
                    fontSize: 16,
                  }}
                />
              </StyledMobileDataItem>
            )}
            {displayFinancialValues && !!totalDeclarationFinancialValuePreview && (
              <StyledMobileDataItem>
                <Value
                  value={totalDeclarationFinancialValuePreview}
                  imgProps={{ src: euroIcon, alt: 'euro icon' }}
                  textProps={{ color: 'primary' }}
                />
              </StyledMobileDataItem>
            )}
            {isGoalMecanicByProduct && (
              <StyledMobileDataItem>
                <Value
                  value={totalDeclarationRewardPreview}
                  textProps={{ color: theme.colors.paletteFromBO.main }}
                />
                <StyledUnitToDisplay>
                  {t('challenges.declaration.toBeWon', { unitToDisplay })}
                </StyledUnitToDisplay>
              </StyledMobileDataItem>
            )}
            {!isGoalMecanicByProduct && isGoalMecanicByProduct !== undefined && (
              <StyledMobileDataItem>
                <Value
                  value={totalDeclarationResultPreview}
                  textProps={{ color: theme.colors.paletteFromBO.main }}
                />
                <StyledUnitToDisplay>
                  {t('challenges.declaration.addedByThisDeclaration', {
                    unitToDisplay,
                  })}
                </StyledUnitToDisplay>
              </StyledMobileDataItem>
            )}
          </StyledMobileDataContainer>
          {expandArrowIcon}
        </StyledMobileView>
      ) : (
        <StyledDesktopView>
          <StyledLeftContainer>
            <StyledPicto>
              <StyledIcon>{icon}</StyledIcon>
            </StyledPicto>
            <StyledSummaryCategory>{title}</StyledSummaryCategory>
            {statement && (
              <StyledNumberOfResultsDeclared>
                {statement}
              </StyledNumberOfResultsDeclared>
            )}
            {basicOverviewStatement && (
              <StyledNumberOfResultsDeclared>
                {basicOverviewStatement}
              </StyledNumberOfResultsDeclared>
            )}
          </StyledLeftContainer>
          <StyledDataContainer>
            {!!totalDeclarationQuantityPreview && (
              <StyledDataItem>
                <Value
                  value={totalDeclarationQuantityPreview}
                  imgProps={{ src: numbersIcon, alt: 'quantity icon' }}
                  textProps={{
                    color: 'secondary',
                    fontSize: 16,
                  }}
                />
              </StyledDataItem>
            )}
            {displayFinancialValues && !!totalDeclarationFinancialValuePreview && (
              <StyledDataItem>
                <Value
                  value={totalDeclarationFinancialValuePreview}
                  imgProps={{ src: euroIcon, alt: 'euro icon' }}
                  textProps={{ color: 'primary' }}
                />
              </StyledDataItem>
            )}
            {isGoalMecanicByProduct && (
              <StyledDataItem>
                <Value
                  value={totalDeclarationRewardPreview}
                  textProps={{ color: theme.colors.paletteFromBO.main }}
                />
                <StyledUnitToDisplay>
                  {t('challenges.declaration.toBeWon', { unitToDisplay })}
                </StyledUnitToDisplay>
              </StyledDataItem>
            )}
            {!isGoalMecanicByProduct && isGoalMecanicByProduct !== undefined && (
              <StyledDataItem>
                <Value
                  value={totalDeclarationResultPreview}
                  textProps={{ color: theme.colors.paletteFromBO.main }}
                />
                <StyledUnitToDisplay>
                  {t('challenges.declaration.addedByThisDeclaration', {
                    unitToDisplay,
                  })}
                </StyledUnitToDisplay>
              </StyledDataItem>
            )}
          </StyledDataContainer>
          <StyledRightContainer>{expandArrowIcon}</StyledRightContainer>
        </StyledDesktopView>
      )}
    </StyledOverviewContainer>
  );
};
